import Cropper, { Area } from 'react-easy-crop'
import { useState, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react'
import { upload_lawyer_photo } from '@/api/lawyer-profile'
import { useTranslation } from 'react-i18next'
import { IconCamera, IconTrash } from '@tabler/icons-react'

// Square crop — 1:1 ratio for profile photo
const FIXED_SIZE = 400
const ASPECT = 1

interface ProfilePhotoUploaderProps {
  initialImage?: string | null
  onUploaded?: (photo_url: string, photo_path: string) => void
}

export interface ProfilePhotoUploaderRef {
  uploadImage: () => Promise<{ photo_url: string; photo_path: string } | null>
  uploading: boolean
}

const ProfilePhotoUploader = forwardRef<ProfilePhotoUploaderRef, ProfilePhotoUploaderProps>(
  ({ initialImage, onUploaded }, ref) => {
    const { t } = useTranslation()
    const [imageSrc, setImageSrc] = useState<string | null>(null)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)

    useEffect(() => {
      if (initialImage) setPreviewUrl(initialImage)
    }, [initialImage])

    const handleFile = (file: File) => {
      setImageSrc(URL.createObjectURL(file))
      setPreviewUrl(null)
    }

    const onCropComplete = useCallback((_: Area, croppedArea: Area) => {
      setCroppedAreaPixels(croppedArea)
    }, [])

    const generateCroppedBlob = useCallback(async (): Promise<Blob | null> => {
      if (!imageSrc || !croppedAreaPixels) return null

      const image = new Image()
      image.src = imageSrc
      await new Promise((res) => (image.onload = res))

      const canvas = document.createElement('canvas')
      canvas.width = FIXED_SIZE
      canvas.height = FIXED_SIZE

      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        FIXED_SIZE,
        FIXED_SIZE
      )

      return new Promise((resolve) =>
        canvas.toBlob((blob) => {
          if (blob) {
            setPreviewUrl(URL.createObjectURL(blob))
            resolve(blob)
          } else {
            resolve(null)
          }
        }, 'image/jpeg')
      )
    }, [imageSrc, croppedAreaPixels])

    // Expose uploadImage to parent (called on Save)
    useImperativeHandle(ref, () => ({
      uploading,
      uploadImage: async () => {
        if (!imageSrc) {
          // No new image selected — return existing URL if available
          if (previewUrl?.startsWith('http')) return null
          return null
        }

        setUploading(true)
        try {
          const blob = await generateCroppedBlob()
          if (!blob) return null

          const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' })
          const result = await upload_lawyer_photo(file)

          if (onUploaded) onUploaded(result.photo_url, result.photo_path)
          setUploading(false)
          return result
        } catch (err) {
          console.error('Photo upload failed:', err)
          setUploading(false)
          return null
        }
      },
    }))

    return (
      <div className="flex flex-col items-center gap-3">

        {/* Preview circle or crop area */}
        <div className="relative">
          {/* Avatar circle */}
          <div className="w-28 h-28 rounded-full border-2 border-border overflow-hidden bg-muted flex items-center justify-center">
            {previewUrl && !imageSrc ? (
              <img src={previewUrl} alt="profile" className="w-full h-full object-cover" />
            ) : imageSrc ? (
              // Show crop box when image selected
              <div className="w-28 h-28 relative">
                <Cropper
                  image={imageSrc}
                  aspect={ASPECT}
                  cropShape="round"
                  crop={crop}
                  zoom={zoom}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: { width: '112px', height: '112px', borderRadius: '50%' },
                  }}
                />
              </div>
            ) : (
              <IconCamera size={32} className="text-muted-foreground" />
            )}
          </div>

          {/* Upload button overlay */}
          {!imageSrc && (
            <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center cursor-pointer shadow-md hover:bg-primary/90 transition-colors">
              <IconCamera size={14} className="text-primary-foreground" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files && handleFile(e.target.files[0])}
              />
            </label>
          )}
        </div>

        {/* Zoom + cancel controls when image is selected */}
        {imageSrc && (
          <div className="flex flex-col items-center gap-2 w-full max-w-xs">
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
            />
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-destructive hover:underline"
              onClick={() => {
                setImageSrc(null)
                setPreviewUrl(initialImage || null)
              }}
            >
              <IconTrash size={12} />
              {t('cancel')}
            </button>
          </div>
        )}

        <p className="text-xs text-muted-foreground">{t('upload_photo_hint', 'JPG, PNG — max 10MB')}</p>
      </div>
    )
  }
)

ProfilePhotoUploader.displayName = 'ProfilePhotoUploader'
export default ProfilePhotoUploader
